import React from "react";
import {
  ThemeProvider as StyledThemeProvider,
  createGlobalStyle,
} from "styled-components";
import { theme } from "../styles/theme";

const GlobalStyles = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@300;400;500;600;700;800&display=swap');
  
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  html {
    scroll-behavior: smooth;
    scrollbar-width: thin;
    scrollbar-color: ${theme.colors.accent.primary} ${theme.colors.surface.secondary};
    overflow-x: hidden;
    width: 100%;
  }
  
  ::-webkit-scrollbar {
    width: 8px;
  }
  
  ::-webkit-scrollbar-track {
    background: ${theme.colors.surface.secondary};
  }
  
  ::-webkit-scrollbar-thumb {
    background: ${theme.colors.accent.primary};
    border-radius: ${theme.borderRadius.full};
    
    &:hover {
      background: ${theme.colors.accent.secondary};
    }
  }
  
  body {
    font-family: ${theme.typography.fonts.primary};
    font-weight: ${theme.typography.weights.normal};
    line-height: ${theme.typography.lineHeights.normal};
    color: ${theme.colors.text.primary};
    background: ${theme.colors.gradients.background};
    min-height: 100vh;
    overflow-x: hidden;
    width: 100%;
    position: relative;
    
    /* Cursor styles for sophisticated dark aesthetic */
    cursor: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20"><circle cx="10" cy="10" r="2" fill="%23dc2626" /></svg>'), auto;
  }
  
  #root {
    min-height: 100vh;
    position: relative;
    overflow-x: hidden;
    width: 100%;
  }
  
  /* Global container constraint to prevent overflow */
  .app {
    overflow-x: hidden;
    width: 100%;
    position: relative;
  }
  
  main {
    overflow-x: hidden;
    width: 100%;
    position: relative;
  }
  
  /* Typography */
  h1, h2, h3, h4, h5, h6 {
    font-family: ${theme.typography.fonts.heading};
    font-weight: ${theme.typography.weights.bold};
    line-height: ${theme.typography.lineHeights.tight};
    color: ${theme.colors.text.primary};
    word-wrap: break-word;
    hyphens: auto;
  }
  
  h1 {
    font-size: ${theme.typography.sizes["6xl"]};
    font-weight: ${theme.typography.weights.extrabold};
  }
  
  h2 {
    font-size: ${theme.typography.sizes["5xl"]};
    font-weight: ${theme.typography.weights.bold};
  }
  
  h3 {
    font-size: ${theme.typography.sizes["3xl"]};
  }
  
  h4 {
    font-size: ${theme.typography.sizes["2xl"]};
  }
  
  h5 {
    font-size: ${theme.typography.sizes.xl};
  }
  
  h6 {
    font-size: ${theme.typography.sizes.lg};
  }
  
  p {
    font-size: ${theme.typography.sizes.base};
    line-height: ${theme.typography.lineHeights.relaxed};
    color: ${theme.colors.text.secondary};
    word-wrap: break-word;
    hyphens: auto;
  }
  
  /* Links */
  a {
    color: ${theme.colors.accent.primary};
    text-decoration: none;
    transition: all ${theme.animations.duration.fast} ${theme.animations.easing.ease};
    
    &:hover {
      color: ${theme.colors.accent.secondary};
    }
  }
  
  /* Form elements */
  input, textarea, select, button {
    font-family: inherit;
    font-size: inherit;
    max-width: 100%;
    box-sizing: border-box;
  }
  
  button {
    cursor: pointer;
    border: none;
    background: none;
    
    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }
  
  /* Images and media */
  img, video, iframe {
    max-width: 100%;
    height: auto;
    display: block;
  }
  
  /* Focus styles */
  *:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${theme.colors.accent.primary};
  }
  
  /* Selection styles */
  ::selection {
    background: ${theme.colors.accent.primary};
    color: ${theme.colors.text.inverse};
  }
  
  ::-moz-selection {
    background: ${theme.colors.accent.primary};
    color: ${theme.colors.text.inverse};
  }
  
  /* Professional utility classes */
  .text-gradient {
    background: ${theme.colors.gradients.primary};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    display: inline-block;
  }
  
  .text-primary {
    color: ${theme.colors.accent.primary};
  }
  
  .text-secondary {
    color: ${theme.colors.accent.secondary};
  }
  
  .surface-elevated {
    background: ${theme.colors.surface.primary};
    box-shadow: ${theme.shadows.elevated};
    border-radius: ${theme.borderRadius.lg};
  }
  
  .glow-primary {
    box-shadow: ${theme.shadows.lg};
  }
  
  /* Professional animation classes */
  .animate-fade-in {
    animation: fadeIn 0.6s ease-out forwards;
  }
  
  .animate-slide-up {
    animation: slideUp 0.8s ease-out forwards;
  }
  
  /* Keyframes */
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  /* Mobile-first responsive design */
  @media (max-width: ${theme.breakpoints.md}) {
    h1 {
      font-size: ${theme.typography.sizes["5xl"]};
    }
    
    h2 {
      font-size: ${theme.typography.sizes["4xl"]};
    }
    
    section {
      padding-left: 1rem;
      padding-right: 1rem;
    }
  }
  
  @media (max-width: ${theme.breakpoints.sm}) {
    h1 {
      font-size: ${theme.typography.sizes["4xl"]};
    }
    
    h2 {
      font-size: ${theme.typography.sizes["3xl"]};
    }
    
    section {
      padding-left: 0.75rem;
      padding-right: 0.75rem;
    }
  }
`;

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  return (
    <StyledThemeProvider theme={theme}>
      <GlobalStyles />
      {children}
    </StyledThemeProvider>
  );
};

export default ThemeProvider;
